import { useTranslation } from 'react-i18next';
import { User } from 'lucide-react';
import {
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@bliks-io/ui';
import { ProfileTab } from './ProfileTab';
import { PreferencesTab } from './PreferencesTab';
import { SecurityTab } from './SecurityTab';
import { useProfile } from '@/api/queries/useProfile';

interface UserSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: 'profile' | 'preferences' | 'security';
}

/**
 * User Settings Dialog
 *
 * Modal dialog for users to manage their profile settings, preferences, and security.
 * Organized into three tabs:
 * - Profile: Edit name, view email/role/department, upload profile picture
 * - Preferences: Change language preference
 * - Security: Change password
 */
export function UserSettingsDialog({
  open,
  onOpenChange,
  initialTab = 'profile'
}: UserSettingsDialogProps) {
  const { t } = useTranslation();
  const { data: user } = useProfile();

  // Don't render dialog content if user data hasn't loaded
  if (!user) {
    return null;
  }

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell size="lg">
        <DialogShellHeader
          icon={<User className="h-5 w-5" />}
          title={t('userSettings.title')}
          subtitle={t('userSettings.description')}
          onClose={handleClose}
        />
        <DialogShellBody>
          <Tabs defaultValue={initialTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="profile">{t('userSettings.tabs.profile')}</TabsTrigger>
              <TabsTrigger value="preferences">{t('userSettings.tabs.preferences')}</TabsTrigger>
              <TabsTrigger value="security">{t('userSettings.tabs.security')}</TabsTrigger>
            </TabsList>

            <div className="min-h-[500px]">
              <TabsContent value="profile" className="mt-6">
                <ProfileTab user={user} onSuccess={handleClose} />
              </TabsContent>

              <TabsContent value="preferences" className="mt-6">
                <PreferencesTab user={user} onSuccess={handleClose} />
              </TabsContent>

              <TabsContent value="security" className="mt-6">
                <SecurityTab onSuccess={handleClose} />
              </TabsContent>
            </div>
          </Tabs>
        </DialogShellBody>
      </DialogShell>
    </Dialog>
  );
}
